import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Search, Pin, MessageSquare, LayoutDashboard, LogOut, User, Shield, Newspaper, LayoutGrid, Settings as SettingsIcon, History, ChevronDown, MessageCircle } from "lucide-react";
import NotificationsBell from "@/components/NotificationsBell";
import LegalNoticeBanner from "@/components/LegalNoticeBanner";
import VerifyEmailBanner from "@/components/VerifyEmailBanner";
import VerifyEmailPrompt from "@/components/VerifyEmailPrompt";

export default function Header({ logoSrc = "/logo-full.png" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [feedCount, setFeedCount] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const isClient = user && user.role === "client";
  const isActive = (p) => location.pathname === p;
  const activeCls = "marker-underline text-[#245544] font-semibold";

  useEffect(() => {
    if (user && (user.role === "client" || user.role === "pro")) {
      api.get("/feed/unseen-count").then(r => setFeedCount(r.data.count || 0)).catch(() => {});
    } else {
      setFeedCount(0);
    }
  }, [user, location.pathname]);

  return (
    <>
    <header className="sticky top-0 z-40 glass-header">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3">
        <Link to="/" className="flex items-center gap-2 group" data-testid="logo-link">
          <img src={logoSrc} alt="LIMYA" className="h-9 w-auto group-hover:scale-105 transition-transform" data-testid="logo-image"/>
          <span className="hidden sm:inline-flex items-center gap-1 px-2 py-[3px] rounded-full bg-white border border-[#D3DAD5] text-[#222321] text-[9px] font-bold uppercase tracking-[0.12em] leading-none" data-testid="public-beta-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2E6B57]"/> Public Beta
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {isClient ? (
            <>
              <Link to="/search" className={`px-4 py-2 rounded-full hover:bg-black/5 text-sm font-medium flex items-center gap-1.5 ${isActive("/search") ? activeCls : ""}`} data-testid="nav-search">
                <Search size={16}/>Browse Pros
              </Link>
              <Link to="/feed" className={`relative px-4 py-2 rounded-full hover:bg-black/5 text-sm font-medium flex items-center gap-1.5 ${isActive("/feed") ? activeCls : ""}`} data-testid="nav-feed-wall">
                <LayoutGrid size={16}/>Feed/Wall
                {feedCount > 0 && location.pathname !== "/feed" && (
                  <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#2E6B57] text-[#FFFFFF] text-[10px] font-bold grid place-items-center" data-testid="feed-unseen-badge">{feedCount > 9 ? "9+" : feedCount}</span>
                )}
              </Link>
              <Link to="/board" className={`px-4 py-2 rounded-full hover:bg-black/5 text-sm font-medium flex items-center gap-1.5 ${isActive("/board") ? activeCls : ""}`} data-testid="nav-my-board">
                <Pin size={16}/>My Board
              </Link>
              <Link to={`/client/${user.id}`} className={`px-4 py-2 rounded-full hover:bg-black/5 text-sm font-medium flex items-center gap-1.5 ${location.pathname === `/client/${user.id}` ? activeCls : ""}`} data-testid="nav-my-profile">
                <User size={16}/>My Profile
              </Link>
              <Link to="/messages" className={`px-4 py-2 rounded-full hover:bg-black/5 text-sm font-medium flex items-center gap-1.5 ${isActive("/messages") ? activeCls : ""}`} data-testid="nav-messages">
                <MessageSquare size={16}/>Messages
              </Link>
            </>
          ) : (
            <>
              <Link to="/search" className="px-4 py-2 rounded-full hover:bg-black/5 text-sm font-medium flex items-center gap-1.5" data-testid="nav-search">
                <Search size={16}/>Browse Pros
              </Link>
              <Link to="/requests" className="px-4 py-2 rounded-full hover:bg-black/5 text-sm font-medium flex items-center gap-1.5" data-testid="nav-requests">
                <LayoutGrid size={16}/>Requests
              </Link>
              {user && user.role === "pro" && (
                <Link to="/feed" className="relative px-4 py-2 rounded-full hover:bg-black/5 text-sm font-medium flex items-center gap-1.5" data-testid="nav-feed">
                  <Newspaper size={16}/>Feed
                  {feedCount > 0 && location.pathname !== "/feed" && (
                    <span className="ml-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#2E6B57] text-[#FFFFFF] text-[10px] font-bold grid place-items-center" data-testid="feed-unseen-badge">{feedCount > 9 ? "9+" : feedCount}</span>
                  )}
                </Link>
              )}
              {user && user.role === "pro" && (
                <Link to="/dashboard" className={`px-4 py-2 rounded-full hover:bg-black/5 text-sm font-medium flex items-center gap-1.5 ${isActive("/dashboard") ? activeCls : ""}`} data-testid="nav-dashboard">
                  <LayoutDashboard size={16}/>Dashboard
                </Link>
              )}
              {user && user.role === "admin" && (
                <Link to="/admin" className="px-4 py-2 rounded-full hover:bg-black/5 text-sm font-medium flex items-center gap-1.5" data-testid="nav-admin">
                  <Shield size={16}/>Admin
                </Link>
              )}
              {user && (
                <Link to="/messages" className={`px-4 py-2 rounded-full hover:bg-black/5 text-sm font-medium flex items-center gap-1.5 ${isActive("/messages") ? activeCls : ""}`} data-testid="nav-messages">
                  <MessageSquare size={16}/>Messages
                </Link>
              )}
            </>
          )}
        </nav>

        <div className="flex items-center gap-2">
          {!user ? (
            <>
              <button onClick={() => navigate("/login")} className="px-5 py-2 text-sm font-semibold hover:bg-black/5 rounded-full" data-testid="header-login-btn">Sign in</button>
              <button onClick={() => navigate("/register")} className="px-5 py-2 text-sm btn-dark" data-testid="header-register-btn">Join LIMYA</button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <NotificationsBell />
              {isClient ? (
                <div className="relative">
                  <button onClick={() => setMenuOpen(o => !o)} className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-black/5" data-testid="header-user-menu">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover"/>
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#2E6B57] grid place-items-center"><User size={14}/></div>
                    )}
                    <span className="text-sm font-medium hidden sm:inline">{user.name?.split(" ")[0]}</span>
                    <ChevronDown size={14} className="text-[#222321]/45"/>
                  </button>
                  {menuOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)}/>
                      <div className="absolute right-0 mt-2 w-52 bg-white rounded-xl border border-[#D3DAD5] shadow-lg py-1.5 z-50 text-sm" data-testid="client-account-menu">
                        <Link to={`/client/${user.id}`} onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3.5 py-2 hover:bg-black/5" data-testid="menu-view-profile"><User size={15}/> View Profile</Link>
                        <Link to="/activity" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3.5 py-2 hover:bg-black/5" data-testid="menu-my-activity"><History size={15}/> Activity &amp; Saved</Link>
                        <Link to="/settings" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 px-3.5 py-2 hover:bg-black/5" data-testid="menu-settings"><SettingsIcon size={15}/> Settings</Link>
                        <Link to="/feedback" onClick={() => setMenuOpen(false)} className="flex items-center gap-2 mx-1.5 my-1 px-2 py-2 rounded-lg bg-[#FDF4E3] border border-[#E0A83E]/40 font-semibold hover:bg-[#FBEBCC] transition-colors" data-testid="menu-feedback"><MessageCircle size={15} className="text-[#E0A83E]"/> Share Feedback</Link>
                        <div className="h-px bg-[#D3DAD5] my-1.5"/>
                        <button onClick={async () => { setMenuOpen(false); await logout(); navigate("/"); }} className="w-full text-left flex items-center gap-2 px-3.5 py-2 hover:bg-black/5 text-red-600" data-testid="menu-sign-out"><LogOut size={15}/> Sign Out</button>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <Link to="/feedback" className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FDF4E3] border border-[#E0A83E]/40 text-[#222321] text-xs font-semibold hover:bg-[#FBEBCC] transition-colors" data-testid="nav-feedback">
                    <MessageCircle size={14} className="text-[#E0A83E]"/> Feedback
                  </Link>
                  <Link to="/settings" className="p-2 rounded-full hover:bg-black/5" data-testid="nav-settings" title="Settings">
                    <SettingsIcon size={16}/>
                  </Link>
                  <Link to={user.role === "pro" ? `/pro/${user.id}` : "/dashboard"} className="flex items-center gap-2 px-3 py-1.5 rounded-full hover:bg-black/5" data-testid="header-user-menu">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="w-7 h-7 rounded-full object-cover"/>
                    ) : (
                      <div className="w-7 h-7 rounded-full bg-[#2E6B57] grid place-items-center"><User size={14}/></div>
                    )}
                    <span className="text-sm font-medium hidden sm:inline">{user.name?.split(" ")[0]}</span>
                  </Link>
                  <button onClick={async () => { await logout(); navigate("/"); }} className="p-2 rounded-full hover:bg-black/5" data-testid="header-logout-btn" title="Sign out">
                    <LogOut size={16}/>
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
      <LegalNoticeBanner />
      <VerifyEmailBanner />
      <VerifyEmailPrompt />
    </>
  );
}
